/**
 * Size Selector Component
 * Handles size dropdown interactions on product pages
 * Directly updates the form's variant ID for reliable cart additions
 */

class SizeSelector {
  constructor() {
    this.selectors = {
      sizeSelector: '.size-selector',
      dropdown: '[data-size-select]',
      variantPicker: 'variant-picker',
      productForm: 'product-form-component form, form[action*="/cart/add"]',
      variantInput: 'input[name="id"]',
      colorSwatch: '.color-swatch--active',
      colorValue: '[data-color-value]'
    };

    this.init();
  }

  init() {
    document.querySelectorAll(this.selectors.sizeSelector).forEach((selector) => {
      this.bindDropdownEvents(selector);
    });

    // Listen for variant updates to sync our dropdown with external changes
    document.addEventListener('variant:update', this.handleVariantUpdate.bind(this));
  }

  bindDropdownEvents(selector) {
    const dropdown = selector.querySelector(this.selectors.dropdown);

    if (dropdown) {
      dropdown.addEventListener('change', (e) => {
        this.handleDropdownChange(e.target, selector);
      });
    }
  }

  handleDropdownChange(dropdown, selector) {
    const sizeName = dropdown.value;
    const productId = selector.dataset.productId;

    if (!sizeName) return;

    // Get the currently selected color
    const selectedColor = this.getSelectedColor();

    // Find the correct variant ID for this color+size combination
    const variantId = this.findVariantId(productId, selectedColor, sizeName);

    if (variantId) {
      // Update URL without page reload
      this.updateURL(variantId);

      // Directly update the form's variant input
      this.updateFormVariantId(variantId);

      // Trigger variant picker for price/image updates (may cause async form update)
      this.triggerVariantPicker(sizeName);

      // Update form again after a short delay to ensure our value takes precedence
      // over any async updates from the theme's variant picker
      setTimeout(() => {
        this.updateFormVariantId(variantId);
      }, 100);
    } else {
      // Fallback: just trigger the variant picker
      this.triggerVariantPicker(sizeName);
    }
  }

  /**
   * Get the currently selected color from the color swatches or variant picker
   */
  getSelectedColor() {
    // First, try to get from our color swatches component
    const colorValue = document.querySelector(this.selectors.colorValue);
    if (colorValue) {
      return colorValue.textContent.trim();
    }

    // Try to get from active color swatch
    const activeSwatch = document.querySelector(this.selectors.colorSwatch);
    if (activeSwatch) {
      return activeSwatch.dataset.color;
    }

    // Try to get from variant picker's color option
    const variantPicker = document.querySelector(this.selectors.variantPicker);
    if (variantPicker) {
      const fieldsets = variantPicker.querySelectorAll('fieldset');
      for (const fieldset of fieldsets) {
        const legend = fieldset.querySelector('legend');
        if (!legend) continue;

        const legendText = legend.textContent.toLowerCase().trim();
        const optionName = legendText.split(/\s+/)[0];

        if (optionName === 'color' || optionName === 'colour') {
          const checkedRadio = fieldset.querySelector('input[type="radio"]:checked');
          if (checkedRadio) {
            return checkedRadio.value;
          }
        }
      }
    }

    return null;
  }

  /**
   * Find the variant ID for a specific color+size combination
   */
  findVariantId(productId, color, size) {
    // Get variant data from the page
    const variantData = this.getVariantData();

    if (!variantData || !variantData.length) {
      return null;
    }

    // Find variant matching both color and size
    for (const variant of variantData) {
      const options = variant.options || [];
      const optionsLower = options.map(opt => opt ? opt.toLowerCase() : '');

      const colorLower = color ? color.toLowerCase() : '';
      const sizeLower = size.toLowerCase();

      // Check if this variant matches both color and size
      const hasColor = !color || optionsLower.includes(colorLower);
      const hasSize = optionsLower.includes(sizeLower);

      if (hasColor && hasSize) {
        return variant.id;
      }
    }

    // If no color specified, find any variant with this size
    for (const variant of variantData) {
      const options = variant.options || [];
      const optionsLower = options.map(opt => opt ? opt.toLowerCase() : '');
      const sizeLower = size.toLowerCase();

      if (optionsLower.includes(sizeLower)) {
        return variant.id;
      }
    }

    return null;
  }

  /**
   * Get variant data from the product JSON on the page
   */
  getVariantData() {
    // First, try to get from our size-selector's variant data
    const sizeSelector = document.querySelector(this.selectors.sizeSelector);
    if (sizeSelector) {
      const variantsJson = sizeSelector.querySelector('[data-product-variants]');
      if (variantsJson) {
        try {
          return JSON.parse(variantsJson.textContent);
        } catch (e) {
          // Continue to fallback
        }
      }
    }

    // Try to get from color-selector's variant data
    const colorSelector = document.querySelector('.color-selector');
    if (colorSelector) {
      const variantsJson = colorSelector.querySelector('[data-product-variants]');
      if (variantsJson) {
        try {
          return JSON.parse(variantsJson.textContent);
        } catch (e) {
          // Continue to fallback
        }
      }
    }

    // Try to get from product JSON script tag
    const productJson = document.querySelector('[type="application/json"][data-product-json]');
    if (productJson) {
      try {
        const product = JSON.parse(productJson.textContent);
        return product.variants;
      } catch (e) {
        // Continue to fallback
      }
    }

    // Try to get from variant picker's JSON
    const variantPicker = document.querySelector(this.selectors.variantPicker);
    if (variantPicker) {
      const jsonScript = variantPicker.querySelector('script[type="application/json"]');
      if (jsonScript) {
        try {
          // This might be just the selected variant, not all variants
          const data = JSON.parse(jsonScript.textContent);
          // If it's a single variant object, we can't use it for lookup
          if (!Array.isArray(data)) {
            return null;
          }
          return data;
        } catch (e) {
          // Continue to fallback
        }
      }
    }

    // Fallback: build variant data from size dropdown options
    // This only works for single-option products
    const dropdown = document.querySelector(this.selectors.dropdown);
    if (dropdown) {
      const variants = [];
      dropdown.querySelectorAll('option[data-variant-id]').forEach(option => {
        if (option.dataset.variantId) {
          variants.push({
            id: parseInt(option.dataset.variantId, 10),
            options: [option.value]
          });
        }
      });
      if (variants.length > 0) {
        return variants;
      }
    }

    return null;
  }

  /**
   * Directly update the form's variant ID input
   */
  updateFormVariantId(variantId) {
    // Find the product form
    const forms = document.querySelectorAll(this.selectors.productForm);

    forms.forEach(form => {
      const variantInput = form.querySelector(this.selectors.variantInput);
      if (variantInput) {
        variantInput.value = variantId;
      }
    });

    // Also update any standalone variant inputs in product-form-component
    const productFormComponent = document.querySelector('product-form-component');
    if (productFormComponent) {
      const variantInput = productFormComponent.querySelector(this.selectors.variantInput);
      if (variantInput) {
        variantInput.value = variantId;
      }
    }
  }

  /**
   * Update the URL with the new variant ID
   */
  updateURL(variantId) {
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variantId);
    window.history.replaceState({}, '', url.toString());
  }

  /**
   * Find the size option in the variant picker and trigger its change
   * This handles price/image updates through the theme's system
   */
  triggerVariantPicker(sizeName) {
    const variantPicker = document.querySelector(this.selectors.variantPicker);

    if (!variantPicker) {
      return;
    }

    // Find all fieldsets (each represents an option like Color, Size, etc.)
    const fieldsets = variantPicker.querySelectorAll('fieldset');

    for (const fieldset of fieldsets) {
      // Check if this fieldset is for size by looking at the legend
      const legend = fieldset.querySelector('legend');
      if (!legend) continue;

      const legendText = legend.textContent.toLowerCase().trim();
      // Remove any swatch value text that might be appended
      const optionName = legendText.split(/\s+/)[0];

      if (optionName === 'size') {
        // Found the size fieldset, now find the matching radio input
        const radioInputs = fieldset.querySelectorAll('input[type="radio"]');

        for (const radio of radioInputs) {
          // Match by value attribute (the size name)
          if (radio.value.toLowerCase() === sizeName.toLowerCase()) {
            // Check the radio and dispatch change event
            radio.checked = true;
            radio.dispatchEvent(new Event('change', { bubbles: true }));
            return;
          }
        }
        return;
      }
    }

    // If no fieldset found, try dropdown select in the variant picker
    const selects = variantPicker.querySelectorAll('select');
    for (const select of selects) {
      const label = variantPicker.querySelector(`label[for="${select.id}"]`);
      if (!label) continue;

      const labelText = label.textContent.toLowerCase().trim();
      if (labelText === 'size') {
        // Found size dropdown, select the matching option
        for (const option of select.options) {
          if (option.value.toLowerCase() === sizeName.toLowerCase()) {
            select.value = option.value;
            select.dispatchEvent(new Event('change', { bubbles: true }));
            return;
          }
        }
      }
    }
  }

  /**
   * Handle variant:update events to sync our dropdown when variant changes externally
   */
  handleVariantUpdate(event) {
    // Re-sync based on the URL variant parameter
    const url = new URL(window.location.href);
    const variantId = url.searchParams.get('variant');

    if (!variantId) return;

    // Find our size selector and update it based on which variant is now selected
    document.querySelectorAll(this.selectors.sizeSelector).forEach((selector) => {
      const dropdown = selector.querySelector(this.selectors.dropdown);
      if (!dropdown) return;

      // Find the option with this variant ID
      const matchingOption = dropdown.querySelector(`option[data-variant-id="${variantId}"]`);

      if (matchingOption) {
        dropdown.value = matchingOption.value;
      }
    });
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new SizeSelector());
} else {
  new SizeSelector();
}

// Re-initialize after Shopify section rendering (for AJAX section updates)
document.addEventListener('shopify:section:load', () => {
  new SizeSelector();
});
